import { Component, EventEmitter, HostBinding, HostListener, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * BasicRadioComponent
 *
 * Purpose
 * - PCC-styled replacement for the default/native radio button control
 * - Provides all interaction states (enabled, hover, focus, error, disabled) for checked and unchecked states
 * - Accessible with keyboard navigation and ARIA attributes
 * - Supports radio button groups via name attribute
 *
 * Visual states
 * - enabled: default state with gray border (unchecked) or purple fill (checked)
 * - hover: light purple border (#DBACF5) when unchecked, same appearance as enabled when checked
 * - focus: blue border (#0671E0) with glow effect (unchecked) or blue fill with glow (checked)
 * - error: red border (unchecked) or red fill (checked)
 * - disabled: grayed out appearance, lighter purple when checked
 *
 * States
 * - unchecked: empty circle with border (16px × 16px outer circle)
 * - checked: filled circle with inner white dot (7px × 7px inner circle)
 *
 * Dimensions
 * - Outer circle: 16px × 16px
 * - Inner circle (when checked): 7px × 7px
 *
 * Features
 * - Radio button groups via name attribute (mutually exclusive selection)
 * - Two-way data binding support
 * - Keyboard accessible (Space/Enter to select)
 * - Error state styling for form validation
 * - Implements ControlValueAccessor for Angular Forms integration
 *
 * Inputs
 * - label: string (optional) - Text displayed to the right of the radio button
 * - name: string (required) - Groups radio buttons together. All radio buttons with the same name are mutually exclusive
 * - value: string (required) - The value this radio button represents
 * - disabled: boolean (default: false) - Disables interaction and applies disabled styling
 * - model: string | null (default: null) - The currently selected value for the radio group (two-way bindable)
 * - hasError: boolean (default: false) - Applies error styling (red border/fill)
 * - checkboxMode: boolean (default: false) - When true, behaves as a checkbox (toggleable) instead of radio button
 * - checkboxModel: boolean (default: false) - The checked state when in checkbox mode (two-way bindable)
 *
 * Outputs
 * - change: string - Emits when the radio button is selected (emits the value)
 * - modelChange: string | null - Supports [(model)] two-way binding syntax
 * - checkboxModelChange: boolean - Supports [(checkboxModel)] two-way binding syntax when in checkbox mode
 *
 * Usage examples
 *
 * <!-- Basic radio buttons in a group -->
 * <app-basic-radio name="size" value="small" label="Small" [(model)]="selectedSize"></app-basic-radio>
 * <app-basic-radio name="size" value="medium" label="Medium" [(model)]="selectedSize"></app-basic-radio>
 * <app-basic-radio name="size" value="large" label="Large" [(model)]="selectedSize"></app-basic-radio>
 *
 * <!-- Radio button with error state -->
 * <app-basic-radio
 *   name="option"
 *   value="option1"
 *   label="Option 1"
 *   [hasError]="true"
 *   [(model)]="selectedOption">
 * </app-basic-radio>
 *
 * <!-- Disabled radio button -->
 * <app-basic-radio
 *   name="option"
 *   value="option2"
 *   label="Option 2"
 *   [disabled]="true"
 *   [(model)]="selectedOption">
 * </app-basic-radio>
 *
 * <!-- Two-way binding with change handler -->
 * <app-basic-radio
 *   name="status"
 *   value="active"
 *   label="Active"
 *   [(model)]="status"
 *   (change)="onStatusChange($event)">
 * </app-basic-radio>
 *
 * <!-- Checkbox mode: toggleable checkbox -->
 * <app-basic-radio
 *   checkboxMode="true"
 *   value="option1"
 *   label="Option 1"
 *   [(checkboxModel)]="isOption1Checked">
 * </app-basic-radio>
 */

@Component({
    selector: 'app-basic-radio',
    templateUrl: './basic-radio.component.html',
    styleUrls: ['./basic-radio.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BasicRadioComponent),
            multi: true
        }
    ],
    standalone: false
})
export class BasicRadioComponent implements ControlValueAccessor {
  @Input() label: string = '';
  @Input() name: string = '';
  @Input() value: string = '';
  @Input() disabled: boolean = false;
  @Input() hasError: boolean = false;
  @Input() checkboxMode: boolean = false;

  @Input() model: string | null = null;
  @Input() checkboxModel: boolean = false;
  @Output() modelChange = new EventEmitter<string | null>();
  @Output() checkboxModelChange = new EventEmitter<boolean>();
  @Output() change = new EventEmitter<string>();

  @HostBinding('attr.role') role = 'radiogroup';
  @HostBinding('class.disabled') get isDisabledHost(): boolean { return this.disabled; }
  @HostBinding('class.has-error') get hasErrorHost(): boolean { return this.hasError; }

  private onChange = (value: string | null) => {};
  private onTouched = () => {};

  get isChecked(): boolean {
    if (this.checkboxMode) {
      return this.checkboxModel;
    }
    return this.model === this.value;
  }

  select(): void {
    if (this.disabled) { return; }

    if (this.checkboxMode) {
      // Checkbox mode: toggle behavior
      this.checkboxModel = !this.checkboxModel;
      this.checkboxModelChange.emit(this.checkboxModel);
      this.change.emit(this.checkboxModel ? this.value : '');
      this.onTouched();
    } else {
      // Radio mode: original behavior (only set if not already checked)
      if (!this.isChecked) {
        this.model = this.value;
        this.modelChange.emit(this.model);
        this.change.emit(this.value);
        this.onChange(this.model);
        this.onTouched();
      }
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) { return; }

    const key = event.key.toLowerCase();
    if (key === ' ' || key === 'enter') {
      event.preventDefault();
      this.select();
    }
  }

  writeValue(value: string | null): void {
    this.model = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

